import React, { useState } from 'react';
import { HomePageStarredEntities } from '@backstage/plugin-home';
import { Content, Page, InfoCard, Link } from '@backstage/core-components';
import {
  makeStyles,
  Typography,
  Collapse,
  IconButton,
  Box,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import DescriptionIcon from '@material-ui/icons/Description';
import AssignmentIcon from '@material-ui/icons/Assignment';
import StorageIcon from '@material-ui/icons/Storage';
import TimelineIcon from '@material-ui/icons/Timeline';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    flexWrap: 'wrap',
  },
  leftColumn: {
    flex: 2,
    minWidth: 300,
  },
  rightColumn: {
    flex: 1,
    minWidth: 280,
  },
  welcomeCard: {
    padding: theme.spacing(3),
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  linkSection: {
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  },
  linkGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  linkItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textDecoration: 'none',
    color: theme.palette.text.primary,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  linkIcon: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: theme.palette.grey[200],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  linkLabel: {
    fontSize: '0.75rem',
    textAlign: 'center',
  },
  usefulLinksTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
}));

interface LinkItem {
  label: string;
  url: string;
  icon: React.ReactNode;
  isExternal?: boolean;
}

interface LinkSectionProps {
  title: string;
  links: LinkItem[];
  defaultExpanded?: boolean;
}

const LinkSection: React.FC<LinkSectionProps> = ({
  title,
  links,
  defaultExpanded = false,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className={classes.linkSection}>
      <div
        className={classes.sectionHeader}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="subtitle1">{title}</Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>
      <Collapse in={expanded}>
        <div className={classes.linkGrid}>
          {links.map((link, index) => {
            const linkProps = link.isExternal
              ? {
                  href: link.url,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                }
              : { href: link.url };

            return (
              <a key={index} {...linkProps} className={classes.linkItem}>
                <div className={classes.linkIcon}>{link.icon}</div>
                <Typography className={classes.linkLabel}>
                  {link.label}
                </Typography>
              </a>
            );
          })}
        </div>
      </Collapse>
    </div>
  );
};

export const HomePage = () => {
  const classes = useStyles();

  // Define your useful links sections - customize these URLs as needed
  const linkSections: {
    title: string;
    links: LinkItem[];
    defaultExpanded?: boolean;
  }[] = [
    {
      title: 'Generic',
      defaultExpanded: true,
      links: [
        { label: 'Docs', url: '/docs', icon: <DescriptionIcon /> },
        { label: 'Tasks', url: '/create/tasks', icon: <AssignmentIcon /> },
      ],
    },
    {
      title: 'Vault',
      links: [
        {
          label: 'Vault UI',
          url: 'https://vault.cloud-tmp.physicsx.ai/',
          icon: <StorageIcon />,
          isExternal: true,
        },
      ],
    },
    {
      title: 'ArgoCD',
      defaultExpanded: true,
      links: [
        {
          label: 'ArgoCD Prod',
          url: 'https://argocd-prod.platform.physicsx.ai/',
          icon: <StorageIcon />,
          isExternal: true,
        },
        {
          label: 'ArgoCD Dev',
          url: 'https://argocd-dev.platform.physicsx.ai/',
          icon: <StorageIcon />,
          isExternal: true,
        },
        {
          label: 'ArgoCD Lab',
          url: 'https://argocd-lab.platform.physicsx.ai/',
          icon: <StorageIcon />,
          isExternal: true,
        },
        {
          label: 'ArgoCD AIWB',
          url: 'https://argocd-aiwb.platform.physicsx.ai/',
          icon: <StorageIcon />,
          isExternal: true,
        },        
      ],
    },
    {
      title: 'Monitoring',
      defaultExpanded: true,
      links: [
        {
          label: 'Grafana',
          url: 'https://health.platform.physicsx.ai/',
          icon: <TimelineIcon />,
          isExternal: true,
        },
        {
          label: 'Platform Dashboards',
          url: 'https://health.platform.physicsx.ai/dashboards/f/ef9rw311hbqiof/?orgId=1',
          icon: <TimelineIcon />,
          isExternal: true,
        },
      ],
    },
  ];

  return (
    <Page themeId="home">
      <Content>
        <div className={classes.container}>
          {/* Left Column */}
          <div className={classes.leftColumn}>
            <InfoCard title="Welcome to NOVA Platform!">
              <Typography paragraph>
                This is where you will find all the necessary resources and
                information to interact with PhysicsX NOVA Platform. Before
                everything you should read our{' '}
                <Link to="/docs">documentation</Link> and get familiar with the
                architecture and the glossary terms.
              </Typography>
              <Typography paragraph>
                Here you will find the <Link to="/catalog">catalog</Link> of PhysicsX NOVA 
                services and resources deployed on the platform, their
                current status, relevant links, links to our external platform
                service providers, <Link to="/create">templates</Link> to create
                or edit tenants/services/resources, etc...
              </Typography>
              <Typography paragraph>
                You can use the search functions to find documentation or
                resources that you are looking for. If you could not find the
                information needed, have a suggestion or find any bug please
                reach out to the Product infrastructure team on the slack channel{' '}
                <Link to="https://slack.com/channel/product-infrastructure">
                  #product-infrastructure
                </Link>
              </Typography>
              <Typography variant="subtitle1">
                <strong>Thank you!</strong>
              </Typography>
            </InfoCard>

            <Box mt={3}>
              <InfoCard title="Your Starred Entities">
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginBottom: 16 }}
                >
                  Click the star beside an entity name to add it to this list!
                </Typography>
                <HomePageStarredEntities />
              </InfoCard>
            </Box>
          </div>

          {/* Right Column */}
          <div className={classes.rightColumn}>
            <Typography variant="h5" className={classes.usefulLinksTitle}>
              Useful Links
            </Typography>
            {linkSections.map((section, index) => (
              <LinkSection
                key={index}
                title={section.title}
                links={section.links}
                defaultExpanded={section.defaultExpanded}
              />
            ))}
          </div>
        </div>
      </Content>
    </Page>
  );
};
